import { useCallback, useMemo, useState } from "react"
import {
  useGetUserSettingsQuery,
  useBulkUpdateUserSettingsMutation,
} from "../../../app/services/api/user_settings"

const parseOrder = (raw) => {
  if (Array.isArray(raw)) return raw
  if (typeof raw !== "string") return null
  try {
    const v = JSON.parse(raw)
    return Array.isArray(v) ? v : null
  } catch {
    return null
  }
}

export const applyOrder = (items, order) => {
  if (!order?.length) return items
  const rest = new Map(items.map((i) => [String(i.id), i]))
  const result = []
  order.forEach((id) => {
    const item = rest.get(String(id))
    if (item) {
      result.push(item)
      rest.delete(String(id))
    }
  })
  items.forEach((i) => {
    if (rest.has(String(i.id))) result.push(i)
  })
  return result
}

const useColumnOrder = (settingKey, items) => {
  const defaultItems = useMemo(() => items ?? [], [items])
  const { data, refetch } = useGetUserSettingsQuery()
  const [bulkUpdate] = useBulkUpdateUserSettingsMutation()
  const [localOrder, setLocalOrder] = useState(null)

  const savedOrder = parseOrder(data?.[settingKey])
  const order = localOrder ?? savedOrder

  const ordered = useMemo(() => applyOrder(defaultItems, order), [defaultItems, order])

  const isDefault = useMemo(
    () => ordered.every((item, i) => item.id === defaultItems[i]?.id),
    [ordered, defaultItems]
  )

  const save = useCallback(
    (ids) => {
      const defaultIds = defaultItems.map((i) => i.id)
      const value = ids.join(",") === defaultIds.join(",") ? [] : ids
      setLocalOrder(value)
      bulkUpdate([{ key: settingKey, value }])
        .unwrap()
        .then(() => refetch())
        .catch(() => setLocalOrder(null))
    },
    [bulkUpdate, refetch, settingKey, defaultItems]
  )

  return { ordered, defaultItems, isDefault, save }
}

export default useColumnOrder
